import { type ComponentProps } from "react"
import { format } from "date-fns"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import UsageWeekGrid from "@/v2-backoffice-client/usage/UsageWeekGrid"
import { deriveWeekRhythm } from "@/v2-backoffice-client/usage/usage.utils"
import {
  AUDIENCE_WINDOW_DAYS,
  type PortfolioAudience as PortfolioAudienceStats,
} from "@/v2-backoffice-client/portfolio/portfolio.audience.utils"

export type PortfolioAudienceProps = ComponentProps<"section"> & {
  /** Derived once by the parent — the KPI row reads the same object. */
  audience: PortfolioAudienceStats
}

// Usage rhythm: the day-by-day DAU strip and how often the monthly actives
// return. The audience SIZE numbers (DAU/WAU/MAU, stickiness) live in the
// KPI row above — this section only carries what a tile can't: the shape
// of the habit over time.
function PortfolioAudience(props: PortfolioAudienceProps) {
  const { audience, className, ...sectionProps } = props

  const maxBand = Math.max(
    1,
    ...audience.frequencyBands.map((band) => band.count),
  )

  const rhythm = deriveWeekRhythm(audience.dauSeries)

  return (
    <section className={cn("flex flex-col gap-4", className)} {...sectionProps}>
      <PortfolioSectionHeader
        eyebrow="Rhythm"
        title="Usage rhythm"
        lead="Who shows up on an ordinary day, and how often the month's people come back."
        info="The daily-actives strip shows distinct users per day over the profile window; the return-frequency bars split the monthly actives by how many days they showed up. Depth is weighted actions per user per active day — wall-clock session duration is not tracked (tracking events carry no session end), so depth per active day is the honest intensity read. Both follow the page's global filters."
      >
        {audience.depthPerActiveDay != null && (
          <span className="ml-auto text-xs text-muted-foreground">
            depth{" "}
            <span className="font-medium text-foreground">
              {formatNumber(Math.round(audience.depthPerActiveDay))}
            </span>{" "}
            weighted actions per user per active day
          </span>
        )}
      </PortfolioSectionHeader>

      <div className="usage-report-grid">
        <UsageReportCard
          title="People per day"
          caption={
            rhythm.peak > 0
              ? `Distinct people with activity on each of the last ${AUDIENCE_WINDOW_DAYS} days. Weekdays average ${formatNumber(Math.round(rhythm.weekdayAverage))} people, weekends ${formatNumber(Math.round(rhythm.weekendAverage))}; the peak was ${formatNumber(rhythm.peak)}${rhythm.peakDate ? ` on ${format(new Date(rhythm.peakDate), "EEEE d MMMM")}` : ""}.`
              : `Distinct people with activity on each of the last ${AUDIENCE_WINDOW_DAYS} days.`
          }
        >
          <UsageWeekGrid series={audience.dauSeries} unit="people" />
        </UsageReportCard>

        <UsageReportCard
          title="How often they come back"
          caption={
            audience.medianActiveDays != null
              ? `The month's people by the number of days they showed up; the typical person came ${formatNumber(audience.medianActiveDays)} days.`
              : "The month's people by the number of days they showed up."
          }
        >
          <ul className="flex flex-col gap-2">
            {audience.frequencyBands.map((band) => (
              <li key={band.key} className="flex items-center gap-2">
                <span className="w-44 shrink-0 truncate text-xs">
                  {band.label}
                </span>
                <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                  <span
                    className="block h-full rounded-full bg-primary"
                    style={{ width: `${(band.count / maxBand) * 100}%` }}
                  />
                </span>
                <span className="w-24 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  {formatNumber(band.count)}
                  {audience.mau > 0 && (
                    <>
                      {" · "}
                      {formatNumber(
                        Math.round((band.count / audience.mau) * 100),
                      )}
                      %
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </UsageReportCard>
      </div>
    </section>
  )
}

export default PortfolioAudience
